/**
 * Statement Orphan Repoint diagnostic card.
 *
 * CLAUDE.md task: routes/management_statements.py's trash icon used to
 * hard-delete a statement doc with no cleanup of statement_payout_matches
 * docs that reference it (fixed alongside this card -- the trash icon now
 * soft-excludes). This card is the write path for the population the
 * read-only Orphaned Links section of the Statement Duplicate Census card
 * already identifies.
 *
 * Candidates for each orphan (same landlord_entity_id + month) are ranked
 * by |transaction.amount - candidate.net_profit|. Apply only ever
 * auto-repoints a match with EXACTLY ONE candidate within tolerance --
 * real prod data has one orphan whose key resolves to five candidates
 * (StatementPayoutMatch carried no property_ref), so most orphans need a
 * human: every orphan's full ranked candidate list is shown, and an
 * operator can manually pick one via radio button + Resolve, even when
 * none were within tolerance. A repoint (auto or manual) backfills
 * property_ref onto the match doc and recomputes the target statement's
 * reconciliation.
 *
 * Deliberately a separate card from the census -- that card is read-only
 * by design (CLAUDE.md section 6, "don't auto-correct evidence") and its
 * own test forbids a re-point/apply control appearing there.
 *
 * Preview-then-apply, same pattern as the Statement Duplicate Cleanup card.
 */

import { useState } from 'react';
import { Loader2, Search, CheckCircle2, AlertTriangle, Unlink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { statementOrphanRepointAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

var REASON_LABEL = {
  no_surviving_twin: 'no surviving twin',
  no_candidate_within_tolerance: 'no candidate within tolerance',
  ambiguous_multiple_within_tolerance: 'ambiguous (multiple within tolerance)',
};

function RepointableRow({ candidate }) {
  return (
    <tr className="border-b border-border/50">
      <td className="py-1.5 pr-3 font-mono text-xs">{candidate.match_id}</td>
      <td className="py-1.5 pr-3 font-mono text-xs">{candidate.transaction_id}</td>
      <td className="py-1.5 pr-3 font-mono text-xs text-muted-foreground">{candidate.old_statement_id}</td>
      <td className="py-1.5 pr-3 font-mono text-xs">{candidate.target_statement_id}</td>
      <td className="py-1.5 pr-3 text-xs text-muted-foreground">
        {candidate.confirm_provenance && candidate.confirm_provenance.confirmed_via
          ? candidate.confirm_provenance.confirmed_via
          : '--'}
      </td>
    </tr>
  );
}

function CandidatePicker({ orphan, onResolve, resolving }) {
  var [selected, setSelected] = useState(null);
  var candidates = orphan.candidates || [];

  return (
    <div className="border border-amber-300 dark:border-amber-800 rounded p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-mono text-muted-foreground">
          match {orphan.match_id} -- old statement {orphan.old_statement_id}
        </p>
        <Badge className="bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 text-[10px]">
          {REASON_LABEL[orphan.reason] || orphan.reason}
        </Badge>
      </div>

      {candidates.length === 0 ? (
        <p className="text-xs text-muted-foreground">No candidate statements found.</p>
      ) : (
        <>
          <div className="space-y-1">
            {candidates.map(function (c) {
              var inputId = orphan.match_id + '__' + c.statement_id;
              return (
                <label key={c.statement_id} htmlFor={inputId} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input
                    type="radio"
                    id={inputId}
                    name={'candidate-' + orphan.match_id}
                    value={c.statement_id}
                    checked={selected === c.statement_id}
                    onChange={() => setSelected(c.statement_id)}
                  />
                  <span className="font-mono">{c.statement_id}</span>
                  <span>{c.property_name_raw || '(unresolved property)'}</span>
                  <span className="text-muted-foreground">net {c.net_profit}</span>
                  <span className="text-muted-foreground">{c.statement_month}</span>
                  <span className={c.within_tolerance ? 'text-green-600' : 'text-muted-foreground'}>
                    diff {c.amount_diff}
                  </span>
                </label>
              );
            })}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={!selected || resolving}
            onClick={() => onResolve(orphan.match_id, selected)}
          >
            {resolving ? <Loader2 className="h-3 w-3 animate-spin mr-2" /> : null}
            Resolve
          </Button>
        </>
      )}
    </div>
  );
}

export function StatementOrphanRepointPanel() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [applying, setApplying] = useState(false);
  var [applyResult, setApplyResult] = useState(null);
  var [resolvingMatchId, setResolvingMatchId] = useState(null);

  var loadPreview = async function () {
    setLoading(true);
    setApplyResult(null);
    try {
      var res = await statementOrphanRepointAPI.preview();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var handleApply = async function () {
    setApplying(true);
    try {
      var res = await statementOrphanRepointAPI.apply();
      var result = res.data;
      toast.success(
        'Re-pointed ' + result.repointed_count + ' match(es), recomputed ' +
        result.recomputed_statement_ids.length + ' statement(s).'
      );
      setApplyResult(result);
      await loadPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  };

  var handleResolve = async function (matchId, targetStatementId) {
    setResolvingMatchId(matchId);
    try {
      await statementOrphanRepointAPI.resolve(matchId, targetStatementId);
      toast.success('Re-pointed ' + matchId + ' to ' + targetStatementId + '.');
      await loadPreview();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResolvingMatchId(null);
    }
  };

  var rePointableCount = data ? data.re_pointable_count : 0;
  var notRePointableCount = data ? data.not_re_pointable_count : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Unlink className="h-4 w-4" />
          Statement Orphan Repoint
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Re-points statement_payout_matches docs left pointing at a dead
          statement_id (see the Orphaned Links section of the Statement
          Duplicate Census card) onto a surviving same-(landlord entity,
          month) twin, ranked by amount difference. Apply only auto-points
          a match with exactly one candidate within tolerance; every other
          orphan is listed with its full ranked candidate list for manual
          resolution below. A repoint backfills property_ref onto the
          match and recomputes the target statement's reconciliation.
        </p>

        <div className="flex gap-2">
          <Button onClick={loadPreview} disabled={loading}>
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          {data && rePointableCount > 0 && (
            <Button
              variant="destructive"
              onClick={handleApply}
              disabled={applying || loading}
            >
              {applying ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Unlink className="h-4 w-4 mr-2" />
              )}
              Re-point {rePointableCount} Orphan{rePointableCount !== 1 ? 's' : ''}
            </Button>
          )}
        </div>

        {data && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              {rePointableCount === 0 && notRePointableCount === 0 ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-muted-foreground">No orphaned matches found</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span>
                    {rePointableCount} auto-pointable orphan{rePointableCount !== 1 ? 's' : ''}
                    {notRePointableCount > 0
                      ? ', ' + notRePointableCount + ' need' + (notRePointableCount === 1 ? 's' : '') + ' manual resolution'
                      : ''}
                  </span>
                </>
              )}
            </div>

            {data.re_pointable && data.re_pointable.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1.5 pr-3 font-medium">Match</th>
                      <th className="py-1.5 pr-3 font-medium">Transaction</th>
                      <th className="py-1.5 pr-3 font-medium">Old Statement</th>
                      <th className="py-1.5 pr-3 font-medium">Target Statement</th>
                      <th className="py-1.5 pr-3 font-medium">Confirmed Via</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.re_pointable.map(function (c) {
                      return <RepointableRow key={c.match_id} candidate={c} />;
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {data.not_re_pointable && data.not_re_pointable.length > 0 && (
              <div className="space-y-2">
                {data.not_re_pointable.map(function (orphan) {
                  return (
                    <CandidatePicker
                      key={orphan.match_id}
                      orphan={orphan}
                      onResolve={handleResolve}
                      resolving={resolvingMatchId === orphan.match_id}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {applyResult && (
          <p className="text-xs text-muted-foreground">
            Last apply: {applyResult.repointed_count} re-pointed, {applyResult.skipped_count} skipped,{' '}
            {applyResult.recomputed_statement_ids.length} statement(s) recomputed at{' '}
            {applyResult.applied_at}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
